import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import './PublicGalleryPage.css';

const PublicGalleryPage = () => {
  const { id } = useParams();
  const [gallery, setGallery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  useEffect(() => {
    const fetchGallery = async () => {
      try {
        const response = await fetch(`https://api.aitomize.space/gallery/${id}/data`);

        if (!response.ok) {
          throw new Error('Gallery not found or expired');
        }

        const data = await response.json();
        setGallery(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchGallery();
  }, [id]);

  const openLightbox = (index) => {
    setCurrentIndex(index);
    setLightboxOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    document.body.style.overflow = 'auto';
  };

  const nextImage = () => {
    setCurrentIndex((prev) => (prev + 1) % gallery.files.length);
  };

  const prevImage = () => {
    setCurrentIndex((prev) => (prev - 1 + gallery.files.length) % gallery.files.length);
  };

  const downloadAll = async () => {
    alert(`Downloading ${gallery.totalFiles} file(s). This may take a moment...`);

    for (let i = 0; i < gallery.files.length; i++) {
      const file = gallery.files[i];
      const link = document.createElement('a');
      link.href = file.originalUrl;
      link.download = file.name;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      await new Promise(resolve => setTimeout(resolve, 500));
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!lightboxOpen) return;
      if (e.key === 'ArrowRight') nextImage();
      if (e.key === 'ArrowLeft') prevImage();
      if (e.key === 'Escape') closeLightbox();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen, gallery]);

  // Touch swipe
  useEffect(() => {
    let touchStartX = 0;
    let touchEndX = 0;

    const handleTouchStart = (e) => {
      touchStartX = e.changedTouches[0].screenX;
    };

    const handleTouchEnd = (e) => {
      touchEndX = e.changedTouches[0].screenX;
      if (touchEndX < touchStartX - 50) nextImage();
      if (touchEndX > touchStartX + 50) prevImage();
    };

    if (lightboxOpen) {
      document.addEventListener('touchstart', handleTouchStart);
      document.addEventListener('touchend', handleTouchEnd);
    }

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [lightboxOpen, gallery]);

  if (loading) {
    return (
      <div className="public-gallery-loading">
        <div className="loading-spinner"></div>
        <p>Loading gallery...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="public-gallery-error">
        <h1>😕 Gallery Not Found</h1>
        <p>{error}</p>
        <p>This gallery may have expired or doesn't exist.</p>
      </div>
    );
  }

  return (
    <div className="public-gallery-page">
      <div className="public-gallery-header">
        <h1>🖼️ {gallery.clientName}</h1>
        <p>{gallery.totalFiles} Photo(s) and Video(s) • It's So Clean Service Documentation</p>
        {gallery.customMessage && (
          <p className="custom-message">"{gallery.customMessage}"</p>
        )}
      </div>

      <div className="public-gallery-grid">
        {gallery.files.map((file, index) => (
          <div
            key={index}
            className="public-gallery-item"
            onClick={() => openLightbox(index)}
          >
            {file.isVideo ? (
              <video src={file.thumbUrl} muted playsInline />
            ) : (
              <img src={file.thumbUrl} alt={file.name} loading="lazy" />
            )}
          </div>
        ))}
      </div>

      <button className="public-download-btn" onClick={downloadAll}>
        📥 Download All
      </button>

      {lightboxOpen && gallery.files[currentIndex] && (
        <div className="public-lightbox active">
          <button className="public-lightbox-close" onClick={closeLightbox}>✕</button>
          <button className="public-lightbox-nav public-lightbox-prev" onClick={prevImage}>‹</button>
          <button className="public-lightbox-nav public-lightbox-next" onClick={nextImage}>›</button>
          <div className="public-lightbox-counter">
            {currentIndex + 1} / {gallery.files.length}
          </div>
          {gallery.files[currentIndex].isVideo ? (
            <video
              className="public-lightbox-content"
              src={gallery.files[currentIndex].originalUrl}
              controls
              autoPlay
            />
          ) : (
            <img
              className="public-lightbox-content"
              src={gallery.files[currentIndex].displayUrl}
              alt={gallery.files[currentIndex].name}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default PublicGalleryPage;
